import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, HStack, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import PolicyFieldValuesForm
  // eslint-disable-next-line max-len
  from '../../../../../../components/RequirementTemplateBuilder/components/RequirementTemplateLeftBox/components/RequirementTemplateBuilderBodyLeft/components/TemplateBuilderPolicyPanel/components/SetPolicyFieldValuesTray/SetPolicyFieldValuesForm/PolicyFieldValuesForm';
import LoadingSpinner from '../../../../../../components/Spinner';
import dataTypes from '../../../../../../constants/dataTypes';
import { fieldInputTypes } from '../../../../../../constants/fieldInputTypes';
import { globalRequirementTemplateBuilder } from '../../../../../../redux/reducer';
import { usePolicyTypeId } from '../../../../../../services/policy-service';
import colors from '../../../../../../styles/theme/foundation/colors';
import { get } from '../../../../../../utils/arrayUtil';
import { isNullOrUndefined } from '../../../../../../utils/validationUtil';

const PolicyBlockEditor = ({ block, onDone }) => {
  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = useState(false);
  const [showError, setShowError] = useState(false);

  const policyTypeId = get(block, 'requirementTemplateBlockProperties.0.requirement.insurancePolicyTypeId');
  const { data: policyType } = usePolicyTypeId(policyTypeId);

  const currentPolicyType = useSelector(globalRequirementTemplateBuilder.selectCurrentPolicyType);
  const currentBlock = useSelector(globalRequirementTemplateBuilder.selectCurrentRequirementTemplateBlock);

  useEffect(() => {
    dispatch(globalRequirementTemplateBuilder.setCurrentRequirementTemplateBlock(block.id));

    return () => {
      dispatch(globalRequirementTemplateBuilder.resetCurrentPolicyType());
      dispatch(globalRequirementTemplateBuilder.resetCurrentRequirementTemplateBlock());
    };
  }, [dispatch, block.id]);

  useEffect(() => {
    if (policyType) {
      dispatch(globalRequirementTemplateBuilder.setCurrentPolicyType(policyType));
    }
  }, [dispatch, policyType]);

  // PolicyFieldValuesForm captures its default values on first render, so wait
  // until the builder store points at this block and its policy type
  const isFormReady = !!currentPolicyType && currentBlock?.[0]?.id === block.id;

  const onSubmit = async data => {
    const updatedBooleanProperties = data[dataTypes.Boolean]?.map((value, id) => ({ id, value }))
      .filter(p => p.value) || [];

    const updatedMonetaryProperties = data[dataTypes.Monetary]?.map((value, id) => {
      const requiredValue = value?.split(/[$,]/)?.join('') ?? null;

      return { id, value: requiredValue };
    })?.filter(p => !isNullOrUndefined(p.value)) || [];

    const updatedAlphaNumericProperties = data[fieldInputTypes.AlphaNumeric]?.map((value, id) => ({ id, value: value || null }))
      .filter(p => !isNullOrUndefined(p.value)) || [];

    const allUpdatedProperties = [
      ...updatedBooleanProperties,
      ...updatedMonetaryProperties,
      ...updatedAlphaNumericProperties
    ];

    if (!allUpdatedProperties.length) {
      setShowError(true);

      return;
    }

    setShowError(false);
    setIsSaving(true);

    await dispatch(globalRequirementTemplateBuilder.updateRequirementTemplateBlock(block.id, {
      required: block.required,
      order: block.order,
      requirement_template_block_properties: allUpdatedProperties.map(property => ({
        ...property,
        requirement_type: 'insurance_policy_type_property',
        requirement_id: property.id
      }))
    }));

    setIsSaving(false);
    onDone();
  };

  const formId = `editExtractedPolicyBlock-${block.id}`;

  return (
    <Box borderRadius="10px" boxShadow="sharp4" layerStyle="panel" mb={4} p={6}>
      <Text mb={2} textStyle="bodyMd600">
        {get(block, 'requirementTemplateBlockProperties.0.policyTypeDisplayName', '')}
      </Text>
      {isFormReady
        ? <PolicyFieldValuesForm blueprint={globalRequirementTemplateBuilder} formId={formId} onSubmit={onSubmit} />
        : <LoadingSpinner isInline minHeight="100px" />}
      {showError && (
        <Text color={colors.error.primary['600']} mt={2} textStyle="bodySm500">
          Policy must have at least one requirement property.
        </Text>
      )}
      <HStack justifyContent="flex-end" mt={4}>
        <Button size="sm" variant="secondary" onClick={onDone}>Cancel</Button>
        <Button
          form={formId}
          isDisabled={!isFormReady || isSaving}
          size="sm"
          type="submit"
        >
          Save Policy
        </Button>
      </HStack>
    </Box>
  );
};

PolicyBlockEditor.propTypes = {
  block: PropTypes.shape({
    id: PropTypes.number,
    order: PropTypes.number,
    required: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    requirementTemplateBlockProperties: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  onDone: PropTypes.func.isRequired
};

export default PolicyBlockEditor;
